import { RWaveLoader } from '@rgrd/component-library'
import { Navigate } from 'react-router'

import { useGetCustomersQuery } from '~/shell/api/rtk/generated'
import { getCustomerPath } from '~/shell/app/paths'
import { useIdentity } from '~/shell/auth/useIdentity'
import { getLastCustomer } from '~/shell/storage/lastCustomer'

import styles from './Bootstrap.module.css'

/**
 * Full-screen branded loader shown while the bootstrap resolves its redirect.
 *
 * Not the shared `PageWaveLoader`: that one is `aria-hidden` and sized for a page body
 * below the header, and this renders instead of the whole chrome.
 */
const BootstrapLoader = () => (
  <main className={styles.loader}>
    <RWaveLoader label="Loading…" />
  </main>
)

/**
 * Post-auth bootstrap for the index route (`/`): decides where a freshly authenticated
 * session lands and redirects there. Renders nothing durable — only a loader while the
 * customer list resolves.
 *
 * Ported from arc, which is the point: the redirect a session lands on is a property of
 * what it is entitled to, not of a constant. Scout previously redirected every session to
 * a hardcoded `demo`, so a user entitled to one real customer still landed on the
 * synthetic one.
 *
 * The four branches are split between the route table and this component:
 *   1. Customer present in the URL → wins. Handled structurally: such a session matches
 *      the `/c/:customer/` route and never reaches this component.
 *   2. Single `customer` claim, no URL customer → route to `/c/<claim>/`.
 *   3. `regard_admin`, no claim → resolve a default customer (last-viewed if still
 *      accessible, else the first in the list) and route to `/c/<default>/`; the header's
 *      picker then lets them switch.
 *   4. Neither admin nor claim → unauthorized.
 */
export const Bootstrap = () => {
  const { isAdmin, customerClaim } = useIdentity()

  // Branch 2: a single customer claim routes straight there, no picker, no list needed.
  if (customerClaim) {
    return <Navigate replace to={getCustomerPath(customerClaim)} />
  }

  // Branch 4: no claim and not an admin — nothing to route to.
  if (!isAdmin) {
    return <Navigate replace to="/unauthorized" />
  }

  // Branch 3: admin with no claim. The default comes from the customer list, so wait for
  // it before redirecting.
  return <AdminBootstrap />
}

/**
 * Branch 3 of the bootstrap: resolve a default customer for an admin who arrived with no
 * customer claim and no URL customer.
 *
 * Prefers the last-viewed customer when it is still in the accessible list, otherwise
 * falls back to the first. Last-viewed is a UX convenience, never an authorization input —
 * the list is what bounds it, and outpatient's own `authorize_customer_access` is the real
 * boundary either way.
 */
const AdminBootstrap = () => {
  const { data, error, isLoading } = useGetCustomersQuery()

  // A 401 means the session is dead; the 401 middleware has already kicked off an
  // interactive login redirect, so hold the loader for the brief moment until the
  // navigation takes over rather than flashing an error page.
  const isUnauthorized =
    error !== undefined && 'status' in error && error.status === 401
  if (isLoading || isUnauthorized) {
    return <BootstrapLoader />
  }

  // Any other failure (or an unexpectedly empty response) is terminal: there is no
  // customer list to land on, so route to the no-access page instead of spinning on the
  // loader forever.
  if (error !== undefined || !data) {
    return <Navigate replace to="/unauthorized" />
  }

  const customers = data.customers

  // An admin with an empty accessible list has nowhere to land.
  if (customers.length === 0) {
    return <Navigate replace to="/unauthorized" />
  }

  const lastViewed = getLastCustomer()
  const isAccessible = customers.some(
    (customer) => customer.customerRegardId === lastViewed,
  )
  const defaultCustomer =
    lastViewed && isAccessible ? lastViewed : customers[0].customerRegardId

  return <Navigate replace to={getCustomerPath(defaultCustomer)} />
}
